import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { PageHeader, Card, Btn, Modal, Field, Input, Select, Badge, EmptyState } from "../components/kr";
import { DatePicker } from "../components/DatePicker";
import { toast } from "sonner";
import { useAuth } from "../lib/auth";
import { Plus, Trash, PencilSimple, CheckCircle, ProhibitInset, TestTube, ArrowCounterClockwise, Clock, SquaresFour, CalendarBlank } from "@phosphor-icons/react";
import { useRequestDelete } from "./Requests";
import { useNavigate, useSearchParams } from "react-router-dom";
import { PRODUCT_UNITS, totalPackets, formatBoxSheetPackets } from "../lib/productUnits";

// ---- Steps-based progress helper ---------------------------------------
// Progress advances only when EVERY linked batch reaches the same step:
//   1. Batch created            — a batch row exists with schedule_id
//   2. Raw material added       — that batch has an rm_audit "add" event
//                                 (i.e. RM was appended via Edit Outputs)
//   3. Finished goods updated   — that batch has ≥1 SKU output row
// Returns { step: 0..3, label, batches: N }.
const STEP_LABELS = [
  "Awaiting batches",
  "Batch created",
  "Raw material added",
  "Finished goods updated",
];

// ---- Date range helper ---------------------------------------------------
// Resolves the active date-filter tab into an inclusive [from, to] pair of
// ISO date strings (YYYY-MM-DD) to compare directly against scheduled_date.
// Returns null for "all" (no filtering) or an unset custom range.
function getDateRangeBounds(filter, customFrom, customTo) {
  const iso = (d) => d.toISOString().slice(0, 10);
  if (filter === "week") {
    const now = new Date();
    const day = now.getDay(); // 0 = Sun .. 6 = Sat
    const diffToMonday = (day === 0 ? -6 : 1) - day;
    const monday = new Date(now);
    monday.setDate(now.getDate() + diffToMonday);
    const sunday = new Date(monday);
    sunday.setDate(monday.getDate() + 6);
    return { from: iso(monday), to: iso(sunday) };
  }
  if (filter === "month") {
    const now = new Date();
    const first = new Date(now.getFullYear(), now.getMonth(), 1);
    const last = new Date(now.getFullYear(), now.getMonth() + 1, 0);
    return { from: iso(first), to: iso(last) };
  }
  if (filter === "custom") {
    if (!customFrom && !customTo) return null; // nothing picked yet — don't filter
    return { from: customFrom || "0000-01-01", to: customTo || "9999-12-31" };
  }
  return null; // "all"
}

function computeScheduleStep(schedule, allBatches) {
  const linked = (allBatches || []).filter(b => b.schedule_id === schedule.id);
  if (linked.length === 0) return { step: 0, label: STEP_LABELS[0], batches: 0, perBatch: [], doneByUnit: {} };
  const allHaveAddedRm = linked.every(b =>
    (b.rm_audit || []).some(a => a.action === "add"));
  const allHaveOutputs = linked.every(b => (b.outputs || []).length > 0);
  let step = 1; // every linked batch is created
  if (allHaveAddedRm) step = 2;
  if (allHaveAddedRm && allHaveOutputs) step = 3;
  // Per-batch step so the tooltip can call out the lagging batch(es).
  const perBatch = linked.map(b => {
    const hasAdd = (b.rm_audit || []).some(a => a.action === "add");
    const hasOutputs = (b.outputs || []).length > 0;
    let s = 1;
    if (hasAdd) s = 2;
    if (hasAdd && hasOutputs) s = 3;
    return { id: b.id, batch_no: b.batch_no || b.id.slice(0, 6), step: s, label: STEP_LABELS[s] };
  }).sort((a, b) => a.step - b.step);   // laggers first
  // Roll up actual production done from every linked batch's SKU outputs,
  // grouped by unit — so a mixed-SKU schedule reads "500 packs · 50 nos"
  // instead of collapsing to the schedule's own unit.
  const doneByUnit = {};
  for (const b of linked) {
    for (const o of (b.outputs || [])) {
      const u = (o.unit || "").trim() || "units";
      const q = parseFloat(o.qty) || 0;
      if (q <= 0) continue;
      doneByUnit[u] = (doneByUnit[u] || 0) + q;
    }
  }
  return { step, label: STEP_LABELS[step], batches: linked.length, perBatch, doneByUnit };
}

export default function Production() {
  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  const navigate = useNavigate();
  const [schedules, setSchedules] = useState([]);
  const [batches, setBatches] = useState([]);
  const [skus, setSkus] = useState([]);
  const [shortages, setShortages] = useState([]);
  const [open, setOpen] = useState(false);
  const [prefill, setPrefill] = useState(null);   // {product, target_qty, sku_ids} → seeds the modal
  const [editing, setEditing] = useState(null);   // Full schedule row when editing
  const [highlightId, setHighlightId] = useState(null); // Schedule id to flash after arrival from ?highlight=…
  const [statusFilter, setStatusFilter] = useState("pending");   // "pending" | "complete" | "waste" | "sample" | "all"
  const [dateFilter, setDateFilter] = useState("all");           // "all" | "week" | "month" | "custom"
  const [customFrom, setCustomFrom] = useState("");
  const [customTo, setCustomTo] = useState("");
  const requestDelete = useRequestDelete();

  const load = async () => {
    const [a, b, s, bt] = await Promise.all([
      api.get("/schedules"),
      api.get("/products"),
      api.get("/production/shortages").catch(() => ({ data: [] })),
      api.get("/batches").catch(() => ({ data: [] })),
    ]);
    setSchedules(a.data); setSkus(b.data); setShortages(s.data || []);
    setBatches(bt.data || []);
  };
  useEffect(() => { load(); }, []);

  // Live "to-do" behaviour: silently refresh the shortages panel, schedules,
  // and linked batches every 30s so the step progress bar advances as new
  // batches are created / RM is added / outputs are saved — no manual reload.
  useEffect(() => {
    const t = setInterval(() => {
      Promise.all([
        api.get("/production/shortages").then(r => setShortages(r.data || [])).catch(() => {}),
        api.get("/schedules").then(r => setSchedules(r.data || [])).catch(() => {}),
        api.get("/batches").then(r => setBatches(r.data || [])).catch(() => {}),
      ]).catch(() => { /* swallow — non-critical */ });
    }, 30000);
    return () => clearInterval(t);
  }, []);

  // Also refresh whenever the tab becomes visible again (operator flipped
  // over to Batches, edited outputs, then came back) so they don't have to
  // wait for the next 30s tick to see the bar catch up.
  useEffect(() => {
    const onVisible = () => {
      if (document.visibilityState === "visible") load();
    };
    document.addEventListener("visibilitychange", onVisible);
    return () => document.removeEventListener("visibilitychange", onVisible);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Deep-link: /production?highlight=<sched_id> from the Batches backlink.
  // Once the schedule row is rendered, scroll to it and pulse a highlight
  // for ~2.5s, then clean the param so a refresh doesn't re-flash.
  const [searchParams, setSearchParams] = useSearchParams();
  useEffect(() => {
    const hid = searchParams.get("highlight");
    if (!hid || !schedules.length) return;
    const exists = schedules.some(s => s.id === hid);
    if (!exists) return;
    setHighlightId(hid);
    setTimeout(() => {
      document.querySelector(`[data-testid="sched-row-${hid}"]`)
        ?.scrollIntoView({ behavior: "smooth", block: "center" });
    }, 100);
    const clearFlash = setTimeout(() => setHighlightId(null), 2500);
    const next = new URLSearchParams(searchParams);
    next.delete("highlight");
    setSearchParams(next, { replace: true });
    return () => clearTimeout(clearFlash);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedules]);

  const scheduleFromShortage = (row) => {
    setEditing(null);
    setPrefill({
      product: row.name,
      target_qty: String(Math.max(1, Math.ceil(row.deficit))),
      sku_ids: [row.sku_id],
    });
    setOpen(true);
  };

  const del = (s) => requestDelete({
    kind: "delete_schedule",
    id: s.id,
    label: `Schedule ${s.product} · ${s.target_qty}${s.unit} · ${s.scheduled_date}`,
    directDeleteUrl: `/schedules/${s.id}`,
    onDone: load,
  });

  const setStatus = async (s, status) => {
    try {
      const r = await api.post(`/schedules/${s.id}/status`, { status });
      const stamped = r?.data?.stamped_batches || 0;
      const noun = status === "pending" ? "Reset to pending"
        : status === "complete" ? "Marked complete"
        : status === "waste" ? "Marked as waste"
        : "Marked as sample";
      toast.success(stamped > 0
        ? `${noun} · ${stamped} linked batch${stamped === 1 ? "" : "es"} tagged`
        : noun);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to update status");
    }
  };

  return (
    <div className="p-6 lg:p-10">
      <PageHeader
        label="PRODUCTION FLOOR"
        title="Production Schedule"
        subtitle="Plan today, this week, this month. Track goal vs. actual."
        action={<Btn onClick={() => { setPrefill(null); setEditing(null); setOpen(true); }} data-testid="add-schedule"><Plus size={14} /> New Schedule</Btn>}
      />

      {/* Sales shortages: SKUs where sales exceed produced stock. Each row
          becomes a one-click "Create schedule" that pre-fills the modal. */}
      {shortages.length > 0 && (
        <Card className="!p-0 mb-6 border-l-4 border-red-500 overflow-hidden" data-testid="shortages-panel">
          <div className="px-4 py-3 bg-red-50 border-b border-red-200 flex items-center justify-between">
            <div>
              <div className="text-[10px] font-mono uppercase tracking-widest text-red-800">Stock shortages</div>
              <div className="text-sm font-bold text-zinc-900">
                {shortages.length} SKU{shortages.length !== 1 ? "s" : ""} need{shortages.length === 1 ? "s" : ""} production
              </div>
            </div>
            <div className="text-[10px] text-zinc-500">Click "Plan batch" to prefill a new schedule</div>
          </div>
          <table className="w-full text-xs">
            <thead className="bg-zinc-50">
              <tr className="text-left text-[10px] font-mono uppercase tracking-widest text-zinc-500">
                <th className="px-4 py-2">SKU</th><th>Sold</th><th>Made</th><th>Deficit</th><th></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-zinc-100">
              {shortages.map(sh => (
                <tr key={sh.sku_id} className="hover:bg-red-50/50" data-testid={`shortage-row-${sh.sku_id}`}>
                  <td className="px-4 py-2">
                    <div className="font-bold">{sh.name}{sh.pack ? <span className="text-zinc-500 font-normal ml-1">· {sh.pack}</span> : null}</div>
                    <div className="text-[10px] font-mono text-zinc-500">{sh.sku_code || "—"}</div>
                  </td>
                  <td className="font-mono">{sh.total_sold}</td>
                  <td className="font-mono">{sh.total_produced}</td>
                  <td className="font-mono font-bold text-red-700">−{sh.deficit}</td>
                  <td className="text-right pr-4">
                    <button
                      onClick={() => scheduleFromShortage(sh)}
                      className="text-[10px] font-mono uppercase tracking-widest bg-red-600 hover:bg-red-700 text-white px-3 py-1 rounded"
                      data-testid={`shortage-plan-${sh.sku_id}`}
                    >Plan batch</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Card>
      )}

      {/* Filter toolbar — status segmented control + period segmented control,
          side by side in one card with a hairline divider between them.
          Picking "Custom" slides open a compact from/to panel underneath.
          Counts reflect the currently loaded schedules list; the date range
          narrows further by scheduled_date. */}
      <div className="mb-4 rounded-xl border border-zinc-200 bg-white shadow-sm overflow-hidden" data-testid="sched-filter-bar">
        <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 px-4 py-3">
          <StatusFilterTabs schedules={schedules} value={statusFilter} onChange={setStatusFilter} />
          <div className="hidden sm:block w-px self-stretch bg-zinc-200" />
          <DateRangeFilter value={dateFilter} onChange={setDateFilter} />
        </div>
        {dateFilter === "custom" && (
          <div className="flex flex-wrap items-end gap-3 px-4 py-3 border-t border-zinc-100 bg-zinc-50/70">
            <div>
              <label className="block text-[9px] font-mono uppercase tracking-widest text-zinc-400 mb-1">From</label>
              <DatePicker value={customFrom} onChange={e => setCustomFrom(e.target.value)} data-testid="sched-date-from" />
            </div>
            <span className="text-zinc-300 pb-2">→</span>
            <div>
              <label className="block text-[9px] font-mono uppercase tracking-widest text-zinc-400 mb-1">To</label>
              <DatePicker value={customTo} onChange={e => setCustomTo(e.target.value)} data-testid="sched-date-to" />
            </div>
            {(customFrom || customTo) && (
              <button
                type="button"
                onClick={() => { setCustomFrom(""); setCustomTo(""); }}
                className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 hover:text-red-600 transition-colors pb-2.5"
                data-testid="sched-date-clear"
              >
                Clear
              </button>
            )}
          </div>
        )}
      </div>

      <Card className="!p-0">
        {(() => {
          const byStatus = statusFilter === "all"
            ? schedules
            : schedules.filter(s => (s.status || "pending") === statusFilter);
          const bounds = getDateRangeBounds(dateFilter, customFrom, customTo);
          const filtered = !bounds
            ? byStatus
            : byStatus.filter(s => s.scheduled_date && s.scheduled_date >= bounds.from && s.scheduled_date <= bounds.to);
          const dateLabel = dateFilter === "week" ? "this week"
            : dateFilter === "month" ? "this month"
            : dateFilter === "custom" ? "the selected range"
            : null;
          if (schedules.length === 0) {
            return <div className="p-6"><EmptyState title="No production scheduled" hint="Add a schedule to set targets." /></div>;
          }
          if (filtered.length === 0) {
            return <div className="p-6"><EmptyState
              title={`No ${statusFilter} schedules${dateLabel ? ` for ${dateLabel}` : ""}`}
              hint={dateLabel ? "Try widening the date range or switching status tabs." : "Try switching to another status tab."}
            /></div>;
          }
          return (
          <table className="kr-table">
            <thead>
              <tr><th>Date</th><th>Product</th><th>Description</th><th>Period</th><th>Target</th><th>Done</th><th>Progress</th><th></th></tr>
            </thead>
            <tbody>
              {filtered.map(s => {
                const { step, label, batches: nBatches, perBatch, doneByUnit } = computeScheduleStep(s, batches);
                const status = s.status || "pending";
                const isClosed = status !== "pending";
                // Effective progress: manual closures (complete/waste/sample)
                // pin the bar full and green; otherwise use the steps math.
                const pct = isClosed ? 100 : (step / 3) * 100;
                const barColor = isClosed
                  ? (status === "complete" ? "bg-green-600"
                    : status === "waste" ? "bg-amber-500"
                    : "bg-blue-500")
                  : (step >= 3 ? "bg-green-600" : step === 0 ? "bg-zinc-300" : "bg-orange-600");
                // Overdue only applies to still-pending schedules.
                const todayStr = new Date().toISOString().slice(0, 10);
                const overdue = !isClosed && !!s.scheduled_date && s.scheduled_date < todayStr && step < 3;
                const tooltipText = perBatch.length === 0
                  ? "No batches linked to this schedule yet"
                  : perBatch.map(pb => `${pb.batch_no} — Step ${pb.step}/3 · ${pb.label}`).join("\n");
                // Status pill styling — reused below the bar when isClosed.
                const statusPill = isClosed && ({
                  complete: { text: "COMPLETE", cls: "bg-green-600 text-white" },
                  waste:    { text: "WASTE",    cls: "bg-amber-500 text-white" },
                  sample:   { text: "SAMPLE",   cls: "bg-blue-500 text-white" },
                }[status]);
                return (
                  <tr
                    key={s.id}
                    data-testid={`sched-row-${s.id}`}
                    className={[
                      overdue ? "bg-red-50/70 border-l-4 border-red-500" : "",
                      isClosed && !overdue ? "opacity-70" : "",
                      highlightId === s.id ? "ring-2 ring-indigo-500 ring-offset-1 animate-pulse" : "",
                    ].filter(Boolean).join(" ")}
                  >
                    <td className="font-mono">
                      <div className="flex items-center gap-2">
                        <span>{s.scheduled_date}</span>
                        {overdue && (
                          <span
                            className="text-[9px] font-mono font-bold uppercase tracking-widest bg-red-600 text-white px-1.5 py-0.5 rounded"
                            data-testid={`sched-overdue-${s.id}`}
                            title={`Scheduled for ${s.scheduled_date} — still at step ${step}/3`}
                          >
                            Overdue
                          </span>
                        )}
                      </div>
                    </td>
                    <td className="font-bold">{s.product}</td>
                    <td
                      className="text-xs text-zinc-600 italic max-w-[220px] truncate"
                      title={s.description || ""}
                      data-testid={`sched-description-${s.id}`}
                    >
                      {s.description || <span className="text-zinc-300 not-italic">—</span>}
                    </td>
                    <td><Badge tone="orange">{s.period}</Badge></td>
                    <td className="font-mono">
                      <div className="whitespace-nowrap">{s.target_qty} {s.unit}</div>
                      {(() => {
                        const approx = formatBoxSheetPackets(totalPackets({ [s.unit || "packets"]: parseFloat(s.target_qty) || 0 }));
                        return approx ? (
                          <div
                            className="text-[10px] text-zinc-500"
                            data-testid={`sched-target-approx-${s.id}`}
                          >
                            ≈ {approx}
                          </div>
                        ) : null;
                      })()}
                    </td>
                    <td className="font-mono" data-testid={`sched-done-${s.id}`}>
                      {(() => {
                        const entries = Object.entries(doneByUnit || {});
                        if (entries.length === 0) {
                          return <span className="text-zinc-400">{s.completed_qty || 0} {s.unit}</span>;
                        }
                        // Raw per-unit rollup (as entered on each batch output row).
                        const raw = entries.map(([u, q]) => {
                          const disp = Number.isInteger(q) ? q : q.toFixed(2);
                          return `${disp} ${u}`;
                        }).join(" · ");
                        // Approx conversion into box · sheet · packets when the
                        // units belong to the finished-goods convention.
                        const approx = formatBoxSheetPackets(totalPackets(doneByUnit));
                        return (
                          <div className="whitespace-nowrap" title="Rolled up from linked batch outputs">
                            <div>{raw}</div>
                            {approx && (
                              <div
                                className="text-[10px] text-zinc-500"
                                data-testid={`sched-done-approx-${s.id}`}
                              >
                                ≈ {approx}
                              </div>
                            )}
                          </div>
                        );
                      })()}
                    </td>
                    <td className="w-56" data-testid={`sched-progress-${s.id}`}>
                      <div className="relative group" data-testid={`sched-progress-wrap-${s.id}`}>
                        <div
                          className="h-2 bg-zinc-100 border border-zinc-200 cursor-help"
                          title={statusPill ? `${statusPill.text} — closed by ${s.status_by || "admin"}` : tooltipText}
                        >
                          <div
                            className={`h-full transition-all ${barColor}`}
                            style={{ width: `${pct}%` }}
                          />
                        </div>
                        {/* Rich hover popover — only when still pending. */}
                        {!isClosed && perBatch.length > 0 && (
                          <div
                            className="hidden group-hover:block absolute z-20 left-0 top-full mt-1 w-64 bg-zinc-900 text-white rounded shadow-lg border border-zinc-700 p-2 space-y-1"
                            data-testid={`sched-progress-tooltip-${s.id}`}
                          >
                            <div className="text-[10px] font-mono uppercase tracking-widest text-zinc-400 mb-1">
                              Batches on this schedule
                            </div>
                            {perBatch.map(pb => (
                              <button
                                type="button"
                                key={pb.id}
                                onClick={() => navigate(`/batches?editBatch=${pb.id}`)}
                                className="w-full flex items-center gap-2 text-[11px] font-mono hover:bg-zinc-800 rounded px-1 py-0.5 text-left"
                                data-testid={`sched-progress-batch-${pb.id}`}
                                title="Open this batch's Edit Outputs modal"
                              >
                                <span className={`inline-block w-1.5 h-1.5 rounded-full flex-shrink-0 ${pb.step >= 3 ? "bg-green-400" : pb.step >= 2 ? "bg-orange-400" : "bg-red-400"}`} />
                                <span className="font-bold">{pb.batch_no}</span>
                                <span className="text-zinc-400">Step {pb.step}/3</span>
                                <span className="ml-auto text-zinc-300 truncate">{pb.label}</span>
                              </button>
                            ))}
                          </div>
                        )}
                      </div>
                      <div className="text-[10px] font-mono mt-0.5 flex items-center justify-between gap-1">
                        {statusPill ? (
                          <span
                            className={`inline-flex items-center px-1.5 py-0.5 rounded font-bold uppercase tracking-widest ${statusPill.cls}`}
                            data-testid={`sched-status-pill-${s.id}`}
                          >
                            {statusPill.text}
                          </span>
                        ) : (
                          <span className={`font-bold ${step >= 3 ? "text-green-700" : step === 0 ? "text-zinc-400" : "text-orange-700"}`}
                                data-testid={`sched-step-label-${s.id}`}>
                            Step {step}/3 · {label}
                          </span>
                        )}
                        <span className="text-zinc-400">
                          {nBatches ? `${nBatches} batch${nBatches === 1 ? "" : "es"}` : "no batches"}
                        </span>
                      </div>
                    </td>
                    <td>
                      <div className="flex items-center gap-1.5 flex-wrap">
                        {/* Status actions — Complete (admin), Waste, Sample.
                            When already closed, show a compact "Reset" (admin). */}
                        {!isClosed ? (
                          <>
                            {isAdmin && (
                              <button
                                onClick={() => setStatus(s, "complete")}
                                className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-green-600 hover:bg-green-700 text-white px-2 py-1 rounded"
                                data-testid={`sched-complete-${s.id}`}
                                title="Force-mark this schedule complete (admin only)"
                              >
                                <CheckCircle size={11} weight="bold" /> Complete
                              </button>
                            )}
                            <button
                              onClick={() => setStatus(s, "waste")}
                              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-amber-500 hover:bg-amber-600 text-white px-2 py-1 rounded"
                              data-testid={`sched-waste-${s.id}`}
                              title="Mark this schedule (and its batches) as waste"
                            >
                              <ProhibitInset size={11} weight="bold" /> Waste
                            </button>
                            <button
                              onClick={() => setStatus(s, "sample")}
                              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-blue-500 hover:bg-blue-600 text-white px-2 py-1 rounded"
                              data-testid={`sched-sample-${s.id}`}
                              title="Mark this schedule (and its batches) as sample"
                            >
                              <TestTube size={11} weight="bold" /> Sample
                            </button>
                          </>
                        ) : (
                          isAdmin && (
                            <button
                              onClick={() => setStatus(s, "pending")}
                              className="inline-flex items-center gap-1 text-[10px] font-mono uppercase tracking-widest bg-zinc-200 hover:bg-zinc-300 text-zinc-800 px-2 py-1 rounded"
                              data-testid={`sched-reset-${s.id}`}
                              title="Reset to pending (admin only)"
                            >
                              <ArrowCounterClockwise size={11} weight="bold" /> Reset
                            </button>
                          )
                        )}
                        <button
                          onClick={() => { setEditing(s); setPrefill(null); setOpen(true); }}
                          data-testid={`edit-sched-${s.id}`}
                          className="text-orange-600 hover:text-orange-800"
                          title="Edit schedule"
                        ><PencilSimple size={14} weight="bold" /></button>
                        <button onClick={() => del(s)} data-testid={`del-sched-${s.id}`} className="text-red-600 hover:text-red-800" title="Delete (admin) / Request delete (others)"><Trash size={14} weight="bold" /></button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          );
        })()}
      </Card>

      {open && <ScheduleModal skus={skus} prefill={prefill} existing={editing}
        onClose={() => { setOpen(false); setPrefill(null); setEditing(null); }}
        onSaved={() => { setOpen(false); setPrefill(null); setEditing(null); load(); }} />}
    </div>
  );
}

function ScheduleModal({ skus, prefill, existing, onClose, onSaved }) {
  const isEdit = !!existing;
  const [f, setF] = useState({
    product: existing?.product || prefill?.product || "",
    target_qty: existing?.target_qty != null ? String(existing.target_qty) : (prefill?.target_qty || "100"),
    scheduled_date: existing?.scheduled_date || new Date().toISOString().slice(0, 10),
    sku_ids: existing?.sku_ids || prefill?.sku_ids || [],
    // Match the unit to whatever the SKU actually outputs (packs / nos / kg / …).
    // Falls back to the existing row's unit, then to "kg" for brand-new schedules
    // without a picked SKU yet — the operator can override it any time.
    unit: existing?.unit || (() => {
      const firstId = (prefill?.sku_ids || [])[0];
      const s = firstId ? skus.find(x => x.id === firstId) : null;
      return s?.unit || "kg";
    })(),
    description: existing?.description || "",
  });
  const upd = (k, v) => setF({ ...f, [k]: v });

  const toggleSku = (id) => setF(prev => {
    const has = prev.sku_ids.includes(id);
    const next = has ? prev.sku_ids.filter(x => x !== id) : [...prev.sku_ids, id];
    // If product name is empty, auto-fill from the first selected SKU's name.
    let product = prev.product;
    let unit = prev.unit;
    if (!product && next.length) {
      const s = skus.find(x => x.id === next[0]);
      if (s) product = s.name;
    }
    // Auto-sync the unit to the first selected SKU whenever the operator hasn't
    // explicitly changed it (i.e. it still matches the previous first-SKU's unit
    // or the default). Editing sticks — we only overwrite when the field looks
    // untouched.
    if (next.length) {
      const s = skus.find(x => x.id === next[0]);
      const prevFirstSku = prev.sku_ids[0] ? skus.find(x => x.id === prev.sku_ids[0]) : null;
      const untouched = !prevFirstSku || prev.unit === prevFirstSku.unit || prev.unit === "kg";
      if (s?.unit && untouched) unit = s.unit;
    }
    return { ...prev, sku_ids: next, product, unit };
  });

  const submit = async (e) => {
    e.preventDefault();
    if (!f.product.trim()) { toast.error("Product name required"); return; }
    try {
      const payload = {
        product: f.product.trim(),
        target_qty: parseFloat(f.target_qty),
        scheduled_date: f.scheduled_date,
        sku_ids: f.sku_ids,
        sku_id: f.sku_ids[0] || null,
        unit: (f.unit || "kg").trim(),
        description: f.description || "",
      };
      if (isEdit) {
        const r = await api.put(`/schedules/${existing.id}`, payload);
        const synced = r?.data?.synced_batches || 0;
        toast.success(synced > 0
          ? `Schedule updated · ${synced} linked batch${synced === 1 ? "" : "es"} synced`
          : "Schedule updated");
      } else {
        await api.post("/schedules", { ...payload, period: "day" });
        toast.success("Schedule added");
      }
      onSaved();
    } catch (e) { toast.error(e.response?.data?.detail || "Failed"); }
  };

  const selectedSkus = skus.filter(s => f.sku_ids.includes(s.id));

  return (
    <Modal open onClose={onClose} title={isEdit ? `Edit schedule — ${existing.product}` : "New Production Schedule"} size="lg">
      <form onSubmit={submit} className="grid grid-cols-2 gap-4" data-testid="sched-form">
        <Field label="Product" full>
          <Input value={f.product} onChange={e => upd("product", e.target.value)} required
                 placeholder="e.g. Potato Chips 100g" data-testid="sched-product" autoFocus />
        </Field>
        <Field label="Target Qty">
          <Input type="number" step="0.01" value={f.target_qty}
                 onChange={e => upd("target_qty", e.target.value)} required data-testid="sched-target" />
        </Field>
        <Field label="Unit">
          <Select
            value={f.unit}
            onChange={e => upd("unit", e.target.value)}
            data-testid="sched-unit"
          >
            {PRODUCT_UNITS.map(u => <option key={u} value={u}>{u}</option>)}
            {/* Preserve any legacy unit already stored on the row so editing
                doesn't silently rewrite it. */}
            {f.unit && !PRODUCT_UNITS.includes(f.unit) && (
              <option value={f.unit}>{f.unit} (legacy)</option>
            )}
          </Select>
        </Field>
        <Field label="Date">
          <DatePicker value={f.scheduled_date}
                 onChange={e => upd("scheduled_date", e.target.value)} required data-testid="sched-date" />
        </Field>
        <Field label="Description (optional notes shown on the schedule row)" full>
          <textarea
            value={f.description}
            onChange={e => upd("description", e.target.value)}
            rows={2}
            placeholder="Short note — e.g. urgent for Retail A, use fresh batch, QA sample first…"
            className="w-full border border-zinc-300 px-2 py-1.5 text-sm rounded focus:outline-none focus:ring-1 focus:ring-orange-500"
            data-testid="sched-description"
          />
        </Field>
        <Field label="SKUs (multi-select, optional)" full>
          <div className="border border-zinc-300 max-h-48 overflow-y-auto bg-white" data-testid="sched-sku-picker">
            {skus.length === 0 ? (
              <div className="p-2 text-xs text-zinc-500 font-mono">No SKUs available</div>
            ) : (
              skus.map(s => (
                <label key={s.id}
                       className="flex items-center gap-2 px-2 py-1.5 hover:bg-zinc-50 cursor-pointer border-b border-zinc-100 last:border-0">
                  <input type="checkbox" checked={f.sku_ids.includes(s.id)} onChange={() => toggleSku(s.id)}
                         className="accent-orange-600" data-testid={`sched-sku-${s.sku_code || s.id}`} />
                  <span className="font-mono text-sm flex-1">{s.name}</span>
                  <span className="text-[10px] font-mono text-zinc-400">{s.sku_code || ""}</span>
                </label>
              ))
            )}
          </div>
          {selectedSkus.length > 0 && (
            <div className="mt-2 flex flex-wrap gap-1">
              {selectedSkus.map(s => (
                <span key={s.id} className="text-[10px] bg-orange-50 text-orange-700 border border-orange-200 px-1.5 py-0.5 font-bold uppercase">
                  {s.sku_code || s.name}
                </span>
              ))}
            </div>
          )}
        </Field>
        <div className="col-span-2 flex justify-end gap-2">
          <Btn type="button" variant="secondary" onClick={onClose}>Cancel</Btn>
          <Btn type="submit" data-testid="sched-submit">{isEdit ? "Save" : "Create"}</Btn>
        </div>
      </form>
    </Modal>
  );
}


// Status segmented control — a single tinted track with a solid, shadowed
// pill for the active tab (rather than five separate outlined buttons).
// Each tab carries its own icon + live count so operators can read the
// state of the floor at a glance before ever switching tabs.
function StatusFilterTabs({ schedules, value, onChange }) {
  const counts = React.useMemo(() => {
    const c = { pending: 0, complete: 0, waste: 0, sample: 0, all: schedules.length };
    for (const s of schedules) c[s.status || "pending"] = (c[s.status || "pending"] || 0) + 1;
    return c;
  }, [schedules]);
  const tabs = [
    { k: "pending",  label: "Pending",  icon: Clock,         active: "bg-orange-600 text-white shadow-sm" },
    { k: "complete", label: "Complete", icon: CheckCircle,   active: "bg-green-600 text-white shadow-sm" },
    { k: "waste",    label: "Waste",    icon: ProhibitInset, active: "bg-amber-500 text-white shadow-sm" },
    { k: "sample",   label: "Sample",   icon: TestTube,      active: "bg-blue-500 text-white shadow-sm" },
    { k: "all",      label: "All",      icon: SquaresFour,   active: "bg-zinc-900 text-white shadow-sm" },
  ];
  return (
    <div className="flex items-center gap-2.5 flex-wrap" data-testid="sched-status-filter">
      <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-400 shrink-0">Status</span>
      <div className="inline-flex items-center gap-0.5 bg-zinc-100 rounded-lg p-1">
        {tabs.map(t => {
          const on = value === t.k;
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              type="button"
              onClick={() => onChange(t.k)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-wide transition-all duration-150 ${on ? t.active : "text-zinc-500 hover:text-zinc-900 hover:bg-white/80"}`}
              data-testid={`sched-status-tab-${t.k}`}
            >
              <Icon size={12} weight={on ? "fill" : "regular"} />
              <span className="font-bold">{t.label}</span>
              <span className={on ? "text-white/70" : "text-zinc-400"}>{counts[t.k] || 0}</span>
            </button>
          );
        })}
      </div>
    </div>
  );
}

// Period segmented control — narrows the schedule list by scheduled_date so
// operators can pull up "This week" / "This month" for a review, or step
// into "Custom" to open the from/to panel rendered by the parent below.
function DateRangeFilter({ value, onChange }) {
  const tabs = [
    { k: "all",    label: "All time" },
    { k: "week",   label: "This week" },
    { k: "month",  label: "This month" },
    { k: "custom", label: "Custom", icon: CalendarBlank },
  ];
  return (
    <div className="flex items-center gap-2.5 flex-wrap" data-testid="sched-date-filter">
      <span className="text-[9px] font-mono uppercase tracking-widest text-zinc-400 shrink-0">Period</span>
      <div className="inline-flex items-center gap-0.5 bg-zinc-100 rounded-lg p-1">
        {tabs.map(t => {
          const on = value === t.k;
          const Icon = t.icon;
          return (
            <button
              key={t.k}
              type="button"
              onClick={() => onChange(t.k)}
              className={`inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-md text-[11px] font-mono uppercase tracking-wide transition-all duration-150 ${on ? "bg-indigo-600 text-white shadow-sm" : "text-zinc-500 hover:text-zinc-900 hover:bg-white/80"}`}
              data-testid={`sched-date-tab-${t.k}`}
            >
              {Icon && <Icon size={12} weight={on ? "fill" : "regular"} />}
              {t.label}
            </button>
          );
        })}
      </div>
    </div>
  );
}